import { useEffect, useMemo, useState } from "react";
import hljs from "highlight.js";
import { apiFetch, BACKEND_URL } from "@/lib/backend";
import { useStore, type Artifact } from "@/lib/store";
import { inTauri } from "@/lib/tools";

type Tab = "code" | "preview";

function languageFor(path: string): string {
  const ext = path.split(".").pop()?.toLowerCase() ?? "";
  const map: Record<string, string> = {
    html: "html", htm: "html", js: "javascript", mjs: "javascript",
    ts: "typescript", tsx: "typescript", jsx: "javascript",
    css: "css", json: "json", md: "markdown", svg: "xml",
  };
  return map[ext] ?? "plaintext";
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

function CopyFileButton({ getCode }: { getCode: () => string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => {
        navigator.clipboard.writeText(getCode());
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
      className="rounded px-2 py-0.5 text-[11px] text-muted-foreground hover:text-foreground"
      title="Copy file contents"
    >
      <i className={`fa-solid ${copied ? "fa-check" : "fa-copy"}`} /> {copied ? "Copied" : "Copy"}
    </button>
  );
}

export function ArtifactDetail({ id, onClose }: { id: string; onClose?: () => void }) {
  const [artifact, setArtifact] = useState<Artifact | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activePath, setActivePath] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>("code");
  const [copied, setCopied] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draftContent, setDraftContent] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const deleteArtifact = useStore((s) => s.deleteArtifact);
  const refreshArtifacts = useStore((s) => s.refreshArtifacts);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await apiFetch(`/api/artifacts/${id}`);
        if (!r.ok) throw new Error(`Status ${r.status}`);
        const data: Artifact = await r.json();
        if (cancelled) return;
        setArtifact(data);
        const paths = Object.keys(data.files);
        setActivePath(paths.includes("index.html") ? "index.html" : paths[0] ?? null);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  useEffect(() => {
    setEditing(false);
    setSaveError(null);
  }, [activePath, id]);

  const previewOrigin = BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
  const previewUrl = `${previewOrigin}/artifacts/${id}/`;

  const highlighted = useMemo(() => {
    if (!artifact || !activePath) return "";
    const raw = artifact.files[activePath];
    const code = typeof raw === "string" ? raw : raw == null ? "" : JSON.stringify(raw, null, 2);
    try {
      return hljs.highlight(code, { language: languageFor(activePath) }).value;
    } catch {
      return hljs.highlightAuto(code).value;
    }
  }, [artifact, activePath]);

  const copyUrl = () => {
    navigator.clipboard.writeText(previewUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const [externalOpened, setExternalOpened] = useState(false);
  const openExternal = async () => {
    if (inTauri) {
      try {
        const { invoke } = await import("@tauri-apps/api/core");
        await invoke("plugin:opener|open_url", { url: previewUrl });
        return;
      } catch {
        // Plugin not installed; fall back to clipboard so the user can paste.
        navigator.clipboard.writeText(previewUrl);
        setExternalOpened(true);
        setTimeout(() => setExternalOpened(false), 2000);
        return;
      }
    }
    window.open(previewUrl, "_blank", "noreferrer");
  };

  const startEdit = () => {
    if (!artifact || !activePath) return;
    setDraftContent(artifact.files[activePath] ?? "");
    setSaveError(null);
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setSaveError(null);
  };

  const saveEdit = async () => {
    if (!artifact || !activePath) return;
    setSaving(true);
    setSaveError(null);
    try {
      const r = await apiFetch(`/api/artifacts/${id}/files/${encodeURIComponent(activePath)}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content: draftContent }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => null);
        throw new Error(body?.error ?? `Save failed: ${r.status}`);
      }
      const data: { artifact: Artifact } = await r.json();
      setArtifact(data.artifact);
      setEditing(false);
      void refreshArtifacts();
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const downloadFile = (path: string) => {
    if (!artifact) return;
    const blob = new Blob([artifact.files[path] ?? ""], { type: "application/octet-stream" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = path.split("/").pop() ?? "file";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleDelete = async () => {
    await deleteArtifact(id);
    setConfirmingDelete(false);
    onClose?.();
  };

  if (error) {
    return <div className="p-4 text-sm text-destructive">Failed to load artifact: {error}</div>;
  }
  if (!artifact) {
    return <div className="p-4 text-sm text-muted-foreground">Loading…</div>;
  }

  const paths = Object.keys(artifact.files);
  const hasIndex = paths.includes("index.html");

  return (
    <div className="flex h-full flex-col">
      <div data-tauri-drag-region className="flex items-center gap-2 border-b px-4 py-2.5" style={{ paddingTop: "calc(0.625rem + var(--titlebar-h, 0px) + env(safe-area-inset-top))" }}>
        <i className="fa-solid fa-file-code text-primary" />
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium">{artifact.name}</div>
          <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
            <span>{paths.length} {paths.length === 1 ? "file" : "files"}</span>
            <span className="opacity-50">·</span>
            <span>{formatBytes(artifact.totalBytes)}</span>
          </div>
        </div>
        {onClose && (
          <button onClick={onClose} className="rounded-md p-1.5 text-muted-foreground hover:text-foreground" title="Close">
            <i className="fa-solid fa-xmark" />
          </button>
        )}
      </div>

      <div className="flex items-center gap-1.5 border-b bg-muted/30 px-3 py-1.5">
        <input
          readOnly
          value={previewUrl}
          onClick={(e) => (e.target as HTMLInputElement).select()}
          className="flex-1 rounded-md border bg-background px-2 py-1 text-xs font-mono outline-none focus:border-primary"
        />
        <button
          onClick={copyUrl}
          className="rounded-md bg-primary px-2.5 py-1 text-xs text-primary-foreground hover:bg-primary/90"
          title="Copy preview URL"
        >
          {copied ? <i className="fa-solid fa-check" /> : <i className="fa-solid fa-copy" />}
        </button>
        <button
          onClick={openExternal}
          className="rounded-md bg-muted px-2.5 py-1 text-xs text-muted-foreground hover:bg-muted/70 hover:text-foreground"
          title={inTauri ? "Open externally (or copy URL)" : "Open in new tab"}
        >
          {externalOpened ? <i className="fa-solid fa-check" /> : <i className="fa-solid fa-arrow-up-right-from-square" />}
        </button>
      </div>

      <div className="flex items-center gap-1 border-b px-3">
        <button
          onClick={() => setTab("code")}
          className={`border-b-2 px-3 py-1.5 text-xs ${tab === "code" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
        >
          Code
        </button>
        <button
          onClick={() => setTab("preview")}
          disabled={!hasIndex}
          className={`border-b-2 px-3 py-1.5 text-xs ${tab === "preview" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground disabled:opacity-30"}`}
        >
          Preview
        </button>
        <div className="ml-auto flex items-center gap-1">
          {confirmingDelete ? (
            <>
              <button
                onClick={handleDelete}
                className="rounded-md bg-destructive px-2 py-1 text-[11px] text-destructive-foreground hover:bg-destructive/90"
              >
                Confirm delete
              </button>
              <button
                onClick={() => setConfirmingDelete(false)}
                className="rounded-md px-2 py-1 text-[11px] text-muted-foreground hover:text-foreground"
              >
                Cancel
              </button>
            </>
          ) : (
            <button
              onClick={() => setConfirmingDelete(true)}
              className="rounded-md p-1.5 text-muted-foreground hover:text-destructive"
              title="Delete artifact"
            >
              <i className="fa-solid fa-trash text-xs" />
            </button>
          )}
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col">
        {tab === "code" ? (
          <>
            <div className="flex items-center gap-0.5 border-b bg-muted/20 px-1 overflow-x-auto">
              {paths.map((p) => (
                <button
                  key={p}
                  onClick={() => setActivePath(p)}
                  className={`group flex items-center gap-1 border-b-2 px-2 py-1.5 text-[11px] whitespace-nowrap ${p === activePath ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
                  title={p}
                >
                  <i className="fa-solid fa-file text-[10px]" />
                  <span className="font-mono">{p}</span>
                  <span
                    role="button"
                    tabIndex={-1}
                    onClick={(e) => { e.stopPropagation(); downloadFile(p); }}
                    className="opacity-0 group-hover:opacity-100 rounded p-0.5 text-muted-foreground hover:text-foreground"
                    title="Download file"
                  >
                    <i className="fa-solid fa-arrow-down-to-line text-[9px]" />
                  </span>
                </button>
              ))}
              <div className="ml-auto flex shrink-0 items-center gap-1 pr-1">
                {saveError && <span className="text-[11px] text-destructive truncate max-w-40">{saveError}</span>}
                {editing ? (
                  <>
                    <button
                      onClick={cancelEdit}
                      disabled={saving}
                      className="rounded px-2 py-0.5 text-[11px] text-muted-foreground hover:text-foreground disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={saveEdit}
                      disabled={saving}
                      className="rounded bg-primary px-2 py-0.5 text-[11px] text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                    >
                      {saving ? "Saving…" : "Save"}
                    </button>
                  </>
                ) : (
                  activePath && (
                    <>
                      <CopyFileButton getCode={() => {
                        const raw = artifact.files[activePath];
                        return typeof raw === "string" ? raw : raw == null ? "" : JSON.stringify(raw, null, 2);
                      }} />
                      <button
                        onClick={startEdit}
                        className="rounded px-2 py-0.5 text-[11px] text-muted-foreground hover:text-foreground"
                        title="Edit file"
                      >
                        <i className="fa-solid fa-pen-to-square" /> Edit
                      </button>
                    </>
                  )
                )}
              </div>
            </div>
            {editing ? (
              <textarea
                value={draftContent}
                onChange={(e) => setDraftContent(e.target.value)}
                spellCheck={false}
                className="w-full flex-1 resize-none border-0 bg-[#0d1117] p-3 font-mono text-xs text-white outline-none"
              />
            ) : (
              <div className="min-w-0 flex-1 overflow-auto bg-[#0d1117]">
                <pre className="p-3 text-xs">
                  <code
                    className={`hljs language-${activePath ? languageFor(activePath) : "plaintext"}`}
                    dangerouslySetInnerHTML={{ __html: highlighted }}
                  />
                </pre>
              </div>
            )}
          </>
        ) : (
          <iframe
            src={previewUrl}
            className="w-full flex-1 border-0 bg-white"
            title={artifact.name}
            sandbox="allow-scripts allow-same-origin"
          />
        )}
      </div>
    </div>
  );
}
